// Victory tracks (ARCHITECTURE §4, docs/research/ideas.md §1). Units differ: space = rungs of 9,
// diplomatic = index, tech / economic / culture = world shares.
//
//   space       rungs of the space ladder achieved by D / 9
//   tech        0.4 AI models (two latest years) + 0.3 TOP500 Rmax (latest list) + 0.3 papers (latest year)
//   economic    0.4 GDP PPP + 0.4 GDP nominal + 0.2 exports
//   diplomatic  0.4 UNGA bloc share + 0.3 diplomatic posts / max + 0.3 defence allies / max
//   culture     0.5 tourist arrivals + 0.3 World Heritage sites + 0.2 Olympic medals (two latest Games)
//   score       equal-weight mean of the five tracks (the UI lets the viewer re-weight)
//
// Missing data:
//   - a state without a value for one input scores 0 on it, and the detail says "no data";
//   - a level is shared out of one common year (indicators.ts shareAt); a state that missed it keeps
//     its own newest value over that year's total, with that year shown;
//   - an input no state has at D (e.g. Lowy posts before 2016) is dropped and the remaining
//     weights are rescaled, and the detail says so.
//
// UNGA bloc share generalises ideas.md §1.5 ("nearest of the leader states"): the share of other
// states whose ideal point is strictly nearer to this state than to any perspective state. For a
// perspective state that is exactly the ideas.md count; ties count for nobody.
//
// dataYear = the oldest data year used, so an entry greys when any of its inputs is over 3 years
// old (ARCHITECTURE §1.5). The space ladder and the World Heritage list are complete through the
// as-of date, so they report year(D).

import type { ActorId, DateStr, Milestone, SourceId, Wonder } from '../core/schema.ts';
import { yearOf } from '../core/dates.ts';
import type { VictoryEntry, VictoryTrack } from './api.ts';
import {
  commonMax, commonValue, series, shareAt, sourcesFor, sourcesOf, totalSourcesOf, windowShare, windowYears, type IndicatorIndex,
} from './indicators.ts';
import type { Why } from './relations.ts';
import { pct, uniq, valueIn } from './util.ts';

/** Rungs on the space ladder (milestones.json, ideas.md §1.2). */
export const SPACE_RUNGS = 9;

/** Two calendar years stand in for "the last 24 months" of Epoch AI models. */
const AI_WINDOW_YEARS = 2;

/** Last Summer + last Winter Games. */
const GAMES_WINDOW = 2;

const LATEST_ONLY = 1;

const IND = {
  ai: 'ai_models',
  top500: 'top500_rmax',
  papers: 'papers',
  ppp: 'gdp_ppp_usd',
  nominal: 'gdp_usd',
  exports: 'exports_usd',
  unga: 'unga_ideal',
  posts: 'embassies',
  tourism: 'tourist_arrivals',
  medals: 'olympic_medals',
} as const;

/** Input weights per track (ARCHITECTURE §4). */
const W = {
  tech: { ai: 0.4, top500: 0.3, papers: 0.3 },
  economic: { ppp: 0.4, nominal: 0.4, exports: 0.2 },
  diplomatic: { unga: 0.4, posts: 0.3, allies: 0.3 },
  culture: { tourism: 0.5, heritage: 0.3, medals: 0.2 },
} as const;

export const BASE_TRACKS = ['space', 'tech', 'economic', 'diplomatic', 'culture'] as const;

export interface VictoryInputs {
  ix: IndicatorIndex;
  milestones: Milestone[];
  wonders: Wonder[];
  /** Perspective states (the UNGA poles). */
  poles: string[];
  /** Defence allies of a state at a date (relations.alliesOf). */
  allies(iso3: string, date: DateStr): Map<ActorId, Why>;
  isCountry(id: ActorId): boolean;
}

/** One weighted input of a track for one state. share = null: no data for this state. */
interface Part {
  key: string;
  weight: number;
  share: number | null;
  text: string;
  year: number | null;
  sources: SourceId[];
}

type PartsFn = (iso3: string) => Part[];

// ─── combining parts ─────────────────────────────────────────────────────────────────────

/** Inputs with data for at least one state; the others are dropped. */
function liveKeys(all: Part[][]): Set<string> {
  const keys = new Set<string>();
  for (const parts of all) {
    for (const p of parts) {
      if (p.share !== null) {
        keys.add(p.key);
      }
    }
  }
  return keys;
}

function combine(iso3: string, parts: Part[], live: Set<string>, year: number): VictoryEntry {
  const used = parts.filter((p) => live.has(p.key));
  const dropped = parts.filter((p) => !live.has(p.key)).map((p) => p.key);
  const wsum = used.reduce((s, p) => s + p.weight, 0);
  const progress = wsum > 0 ? used.reduce((s, p) => s + p.weight * (p.share ?? 0), 0) / wsum : 0;

  const years = used.map((p) => p.year).filter((y): y is number => y !== null);
  const text = used.map((p) => p.text);
  if (dropped.length > 0) {
    text.push(`not scored (no data at this date): ${dropped.join(', ')}`);
  }
  return {
    iso3,
    progress,
    detail: text.join(' · '),
    dataYear: years.length > 0 ? Math.min(...years) : year,
    sources: uniq(used.flatMap((p) => (p.share === null ? [] : p.sources))),
  };
}

function rank(entries: VictoryEntry[]): VictoryEntry[] {
  return entries.sort((a, b) => b.progress - a.progress || (a.iso3 < b.iso3 ? -1 : 1));
}

function scoreParts(states: string[], fn: PartsFn, year: number): VictoryEntry[] {
  const all = states.map(fn);
  const live = liveKeys(all);
  return rank(states.map((iso3, i) => combine(iso3, all[i], live, year)));
}

// ─── part builders ───────────────────────────────────────────────────────────────────────

function noData(key: string, weight: number, label: string): Part {
  return { key, weight, share: null, text: `${label}: no data`, year: null, sources: [] };
}

/** Level share: the state's latest value ≤ year over the world total. */
function levelPart(v: VictoryInputs, id: string, weight: number, label: string, iso3: string, year: number): Part {
  const s = shareAt(v.ix, id, iso3, year);
  if (!s) {
    return noData(label, weight, label);
  }
  const sources = uniq([...sourcesFor(v.ix, id, iso3, s.year), ...totalSourcesOf(v.ix, id, s.year)]);
  return { key: label, weight, share: s.share, text: `${label} ${pct(s.share)} (${s.year})`, year: s.year, sources };
}

/** Count share over the latest `n` years with a world value. */
function countPart(v: VictoryInputs, id: string, n: number, weight: number, label: string, iso3: string, year: number): Part {
  const s = windowShare(v.ix, id, iso3, year, n);
  if (!s) {
    return noData(label, weight, label);
  }
  const span = s.years.length > 1 ? `${s.years[0]}–${s.years[s.years.length - 1]}` : String(s.years[0]);
  const text = `${label} ${pct(s.share)} (${span})`;
  const sources = uniq(s.years.flatMap((y) => [...sourcesFor(v.ix, id, iso3, y), ...totalSourcesOf(v.ix, id, y)]));
  return { key: label, weight, share: s.share, text, year: s.years[s.years.length - 1], sources };
}

/** value / largest value among states. */
function ratioPart(key: string, weight: number, value: number | null, max: number, year: number | null, sources: SourceId[]): Part {
  if (value === null || !(max > 0)) {
    return noData(key, weight, key);
  }
  return { key, weight, share: value / max, text: `${key} ${value}/${max}${year === null ? '' : ` (${year})`}`, year, sources };
}

// ─── space ───────────────────────────────────────────────────────────────────────────────

function spaceEntries(v: VictoryInputs, states: string[], date: DateStr): VictoryEntry[] {
  const reached = v.milestones.filter((m) => m.track === 'space' && m.rung !== undefined && m.date <= date);
  const ladder = uniq(reached.flatMap((m) => m.sources));
  const year = yearOf(date);

  return rank(states.map((iso3) => {
    const mine = reached.filter((m) => m.iso3 === iso3);
    const rungs = new Set(mine.map((m) => m.rung));
    const last = mine.reduce<Milestone | null>((a, m) => (a === null || m.date > a.date ? m : a), null);
    const tail = last ? ` · last: ${last.title} (${last.date})` : '';
    return {
      iso3,
      progress: rungs.size / SPACE_RUNGS,
      detail: `${rungs.size} of ${SPACE_RUNGS} space-ladder rungs${tail}`,
      dataYear: year,
      sources: mine.length > 0 ? uniq(mine.flatMap((m) => m.sources)) : ladder,
    };
  }));
}

// ─── tech, economic, culture ─────────────────────────────────────────────────────────────

function techParts(v: VictoryInputs, year: number): PartsFn {
  return (iso3) => [
    countPart(v, IND.ai, AI_WINDOW_YEARS, W.tech.ai, 'AI models', iso3, year),
    countPart(v, IND.top500, LATEST_ONLY, W.tech.top500, 'TOP500 Rmax', iso3, year),
    countPart(v, IND.papers, LATEST_ONLY, W.tech.papers, 'papers', iso3, year),
  ];
}

function economicParts(v: VictoryInputs, year: number): PartsFn {
  return (iso3) => [
    levelPart(v, IND.ppp, W.economic.ppp, 'GDP PPP', iso3, year),
    levelPart(v, IND.nominal, W.economic.nominal, 'GDP nominal', iso3, year),
    levelPart(v, IND.exports, W.economic.exports, 'exports', iso3, year),
  ];
}

interface Heritage {
  listed: number;
  counts: Map<string, number>;
  sources: SourceId[];
}

/** Sites inscribed by `year`, counted once per state they span. */
function heritageAt(v: VictoryInputs, year: number): Heritage {
  const listed = v.wonders.filter((w) => w.year <= year);
  const counts = new Map<string, number>();
  for (const w of listed) {
    w.iso3s.forEach((iso3) => counts.set(iso3, (counts.get(iso3) ?? 0) + 1));
  }
  return { listed: listed.length, counts, sources: uniq(listed.flatMap((w) => w.sources)) };
}

function heritagePart(h: Heritage, iso3: string, year: number): Part {
  if (h.listed === 0) {
    return noData('World Heritage', W.culture.heritage, 'World Heritage');
  }
  const n = h.counts.get(iso3) ?? 0;
  return { key: 'World Heritage', weight: W.culture.heritage, share: n / h.listed, text: `World Heritage ${n}/${h.listed}`, year, sources: h.sources };
}

function cultureParts(v: VictoryInputs, year: number): PartsFn {
  const heritage = heritageAt(v, year);
  return (iso3) => [
    levelPart(v, IND.tourism, W.culture.tourism, 'tourist arrivals', iso3, year),
    heritagePart(heritage, iso3, year),
    countPart(v, IND.medals, GAMES_WINDOW, W.culture.medals, 'Olympic medals', iso3, year),
  ];
}

// ─── diplomatic ──────────────────────────────────────────────────────────────────────────

/** Ideal points of every state at the newest session year ≤ year. */
function ungaPoints(v: VictoryInputs, states: string[], year: number): { year: number; points: Map<string, number> } | null {
  const [session] = windowYears(v.ix, IND.unga, year, LATEST_ONLY);
  if (session === undefined) {
    return null;
  }
  const points = new Map<string, number>();
  for (const iso3 of states) {
    const p = valueIn(series(v.ix, IND.unga, iso3), session);
    if (p !== null) {
      points.set(iso3, p);
    }
  }
  return { year: session, points };
}

/** Share of other states strictly nearer to `iso3` than to any pole (see header). */
function blocShare(points: Map<string, number>, poles: string[], iso3: string): number | null {
  const me = points.get(iso3);
  if (me === undefined) {
    return null;
  }
  const rivals = poles.filter((p) => p !== iso3 && points.has(p)).map((p) => points.get(p) as number);
  let n = 0;
  let near = 0;
  for (const [s, x] of points) {
    if (s === iso3 || poles.includes(s)) {
      continue;
    }
    n++;
    const d = Math.abs(x - me);
    if (rivals.every((r) => d < Math.abs(x - r))) {
      near++;
    }
  }
  return n > 0 ? near / n : null;
}

function allyCounts(v: VictoryInputs, states: string[], date: DateStr): Map<string, { n: number; sources: SourceId[] }> {
  const out = new Map<string, { n: number; sources: SourceId[] }>();
  for (const iso3 of states) {
    const allies = [...v.allies(iso3, date)].filter(([id]) => v.isCountry(id));
    out.set(iso3, { n: allies.length, sources: uniq(allies.flatMap(([, w]) => w.sources)) });
  }
  return out;
}

/** The diplomatic parts that hold for a whole year: UNGA bloc and posts, per state. */
function yearlyDiplomatic(v: VictoryInputs, states: string[], year: number): Map<string, Part[]> {
  const unga = ungaPoints(v, states, year);
  const postsMax = commonMax(v.ix, IND.posts, year);
  const ungaSources = sourcesOf(v.ix, IND.unga);

  return new Map(states.map((iso3) => {
    const share = unga ? blocShare(unga.points, v.poles, iso3) : null;
    const bloc: Part = share === null || !unga
      ? noData('UNGA bloc', W.diplomatic.unga, 'UNGA bloc')
      : { key: 'UNGA bloc', weight: W.diplomatic.unga, share, text: `UNGA bloc ${pct(share)} (${unga.year})`, year: unga.year, sources: ungaSources };
    // Posts are a ratio to the most of any state, so only states of the common year count.
    const post = commonValue(v.ix, IND.posts, iso3, year);
    const posts = ratioPart('diplomatic posts', W.diplomatic.posts, post ? post[1] : null, postsMax, post ? post[0] : null, post ? sourcesFor(v.ix, IND.posts, iso3, post[0]) : []);
    return [iso3, [bloc, posts]];
  }));
}

/** Allies are the one diplomatic input that turns over within a year, so they are counted per date. */
function diplomaticParts(v: VictoryInputs, states: string[], date: DateStr, yearly: Map<string, Part[]>): PartsFn {
  const allies = allyCounts(v, states, date);
  const alliesMax = Math.max(0, ...[...allies.values()].map((a) => a.n));

  return (iso3) => {
    const mine = allies.get(iso3) ?? { n: 0, sources: [] };
    return [...(yearly.get(iso3) ?? []), ratioPart('defence allies', W.diplomatic.allies, mine.n, alliesMax, null, mine.sources)];
  };
}

// ─── score ───────────────────────────────────────────────────────────────────────────────

/** Equal-weight mean of the base tracks (pass them in BASE_TRACKS order). */
export function scoreEntries(tracks: VictoryEntry[][]): VictoryEntry[] {
  const byIso = new Map<string, VictoryEntry[]>();
  for (const list of tracks) {
    for (const e of list) {
      byIso.set(e.iso3, [...(byIso.get(e.iso3) ?? []), e]);
    }
  }
  return rank([...byIso].map(([iso3, es]) => ({
    iso3,
    progress: es.reduce((s, e) => s + e.progress, 0) / tracks.length,
    detail: `equal weights over ${BASE_TRACKS.join(', ')}: ${es.map((e) => pct(e.progress)).join(' · ')}`,
    dataYear: Math.max(...es.map((e) => e.dataYear)),
    sources: uniq(es.flatMap((e) => e.sources)),
  })));
}

// ─── entry point ─────────────────────────────────────────────────────────────────────────

/** Victory entries for the states of a date (see `VictoryFn`). */
export type VictoryFn = (states: string[], date: DateStr, track: VictoryTrack) => VictoryEntry[];

/** What is kept per set of states (the array is shared by every date of one border picture) and year. */
type Yearly<T> = WeakMap<string[], Map<string, T>>;

function kept<T>(memo: Yearly<T>, states: string[], key: string, make: () => T): T {
  const byKey = memo.get(states) ?? new Map<string, T>();
  memo.set(states, byKey);

  const hit = byKey.get(key);
  if (hit) {
    return hit;
  }

  const made = make();
  byKey.set(key, made);
  return made;
}

/**
 * Victory entries for `states` (the states that exist at `date`), best first. A track that depends
 * on the year alone is worked out once per year and set of states, so scrubbing the weeks of a year
 * repeats nothing but the ally counts and the space ladder; the entries are shared, read-only.
 */
export function victoryModel(v: VictoryInputs): VictoryFn {
  const tables: Yearly<VictoryEntry[]> = new WeakMap();
  const diplomatic: Yearly<Map<string, Part[]>> = new WeakMap();

  const yearlyTable = (states: string[], year: number, track: VictoryTrack, parts: () => PartsFn): VictoryEntry[] =>
    kept(tables, states, `${track}|${year}`, () => scoreParts(states, parts(), year));

  const model: VictoryFn = (states, date, track) => {
    const year = yearOf(date);
    if (track === 'space') {
      return spaceEntries(v, states, date);
    }
    if (track === 'tech') {
      return yearlyTable(states, year, track, () => techParts(v, year));
    }
    if (track === 'economic') {
      return yearlyTable(states, year, track, () => economicParts(v, year));
    }
    if (track === 'diplomatic') {
      const yearly = kept(diplomatic, states, String(year), () => yearlyDiplomatic(v, states, year));
      return scoreParts(states, diplomaticParts(v, states, date, yearly), year);
    }
    if (track === 'culture') {
      return yearlyTable(states, year, track, () => cultureParts(v, year));
    }
    return scoreEntries(BASE_TRACKS.map((t) => model(states, date, t)));
  };
  return model;
}
